import { readonly, shallowRef, } from 'vue';
import { ENV_TYPE, } from '@/enums/configs';
import { store, useSessionStore, } from '@/store';
import { getEnv, } from '@/utils/env';
import {
  loadLoginSDK,
  type LoginSDKConstructor,
  type LoginSDKInstance,
} from '@/utils/loginSDK';

type LoginSDKStatus = 'idle' | 'loading' | 'ready' | 'failed';

const loginSDKStatusState = shallowRef<LoginSDKStatus>('idle');

export const loginSDKStatus = readonly(loginSDKStatusState);

const getLoginSDKEnv = (): 'test' | 'stg' | 'prod' => {
  const env = getEnv();

  if (env === ENV_TYPE.PROD) {
    return 'prod';
  }
  if (env === ENV_TYPE.STG) {
    return 'stg';
  }
  return 'test';
};

const setLoginSDKVisibility = (
  elements: HTMLElement[],
  visible: boolean
) => {
  elements.forEach((element) => {
    element.style.display = visible ? '' : 'none';
  });
};

const parseUserInfo = (user: unknown): Record<string, unknown> | null => {
  if (user === null || user === undefined || user === '') {
    return null;
  }

  const parsedUser = typeof user === 'string' ? JSON.parse(user) : user;

  if (typeof parsedUser !== 'object' || parsedUser === null) {
    throw new TypeError('loginSDK user info is invalid');
  }

  return parsedUser as Record<string, unknown>;
};

export const setupLoginSDK = async () => {
  const scene = new URLSearchParams(window.location.search).get('scene');

  if (scene !== 'office') {
    return;
  }

  const header = document.getElementById('header');
  const footer = document.getElementById('footer');

  if (!header || !footer) {
    loginSDKStatusState.value = 'failed';
    return;
  }

  const loginSDKElements = [header, footer,];
  const sessionStore = useSessionStore(store);
  // Callbacks during init can call hideLoginSDK without returning from setupLoginSDK;
  // track that locally so TS doesn't treat the later 'ready' gate as unreachable.
  let setupFailed = false;
  const hideLoginSDK = () => {
    setupFailed = true;
    loginSDKStatusState.value = 'failed';
    setLoginSDKVisibility(loginSDKElements, false);
  };
  const storeTokenFromUser = (user: unknown) => {
    const userInfo = parseUserInfo(user);

    if (!userInfo) {
      return false;
    }

    return sessionStore.setAccessToken(userInfo.access_token);
  };
  const handleLogin = (user: unknown, reload: boolean) => {
    try {
      const tokenStored = storeTokenFromUser(user);

      if (tokenStored && reload) {
        window.location.reload();
      }
    } catch (error) {
      console.warn('Failed to parse loginSDK callback', error);
      hideLoginSDK();
    }
  };

  loginSDKStatusState.value = 'loading';
  setLoginSDKVisibility(loginSDKElements, false);

  let LoginSDK: LoginSDKConstructor;

  try {
    LoginSDK = await loadLoginSDK();
  } catch (error) {
    console.warn('Failed to load loginSDK', error);
    hideLoginSDK();
    return;
  }

  let loginSDKInstance: LoginSDKInstance;

  try {
    loginSDKInstance = new LoginSDK({
      header,
      footer,
      env: getLoginSDKEnv(),
      onLogin: (user) => handleLogin(user, false),
      onSDKLogin: (user) => handleLogin(user, true),
    });
  } catch (error) {
    console.warn('Failed to initialize loginSDK', error);
    hideLoginSDK();
    return;
  }

  let userInfo: unknown;

  try {
    if (typeof loginSDKInstance.getUserInfo !== 'function') {
      throw new TypeError('loginSDK getUserInfo is unavailable');
    }
    userInfo = await loginSDKInstance.getUserInfo();
  } catch (error) {
    console.warn('Failed to get loginSDK user info', error);
    hideLoginSDK();
    return;
  }

  try {
    storeTokenFromUser(userInfo);
  } catch (error) {
    console.warn('Failed to parse loginSDK user info', error);
    hideLoginSDK();
    return;
  }

  if (setupFailed) {
    return;
  }

  loginSDKStatusState.value = 'ready';
  setLoginSDKVisibility(loginSDKElements, true);
};
